import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Clock, CalendarOff, Trash2, AlertTriangle, Settings2, CheckCircle2, Wrench } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAY_LABELS = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };
const DAY_ORDER = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

function detailMsg(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object") return d.message || fallback;
  return fallback;
}

export default function ScheduleSettings({ token, onAuthError }) {
  const [cfg, setCfg] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [staleSlots, setStaleSlots] = useState([]);
  const [staleLoading, setStaleLoading] = useState(false);
  const [repairing, setRepairing] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ start_date: "", end_date: "", full_day: true, start_time: "12:00", end_time: "15:00", label: "" });
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = () => {
    Promise.all([
      axios.get(`${API}/schedule/config`, auth),
      axios.get(`${API}/schedule/blocks`, auth),
      axios.get(`${API}/schedule/conflicts`, auth),
    ]).then(([c, b, cf]) => { setCfg(c.data); setBlocks(b.data); setConflicts(cf.data); })
      .catch((e) => { if (e.response?.status === 401) onAuthError?.(); else toast.error("Could not load schedule settings."); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); loadStale(); /* eslint-disable-next-line */ }, []);

  const loadStale = () => {
    setStaleLoading(true);
    axios.get(`${API}/schedule/slot-audit`, auth)
      .then((r) => setStaleSlots(r.data.stale_slots || []))
      .catch(() => {})
      .finally(() => setStaleLoading(false));
  };

  const repairSlot = async (row) => {
    const reason = window.prompt(
      `Repair ${row.date} ${row.time}: ${row.stored_count} reserved but only ${row.true_count} active appointment(s) genuinely need it ` +
      `(${row.stale_reservations} orphaned reservation${row.stale_reservations === 1 ? "" : "s"} — e.g. left behind by a reset/cancelled extension). ` +
      `This only lowers THIS slot's count to ${row.true_count}; it never touches any booking's dates, charges, or other slots. Enter a note to confirm:`, "Stale slot cleanup");
    if (reason === null) return;
    const key = `${row.date}T${row.time}`;
    setRepairing(key);
    try {
      const { data } = await axios.post(`${API}/schedule/slot-repair`, { date: row.date, time: row.time, reason }, auth);
      toast.success(`Repaired ${row.date} ${row.time}: ${data.old_count} → ${data.new_count}.`);
      loadStale();
    } catch (err) { toast.error(detailMsg(err, "Could not repair this slot.")); }
    finally { setRepairing(""); }
  };

  const saveCfg = async (patch) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/schedule/config`, patch, auth);
      setCfg(data);
      toast.success("Schedule updated.");
    } catch (err) { toast.error(detailMsg(err, "Could not save.")); }
    finally { setSaving(false); }
  };

  const setDayHour = (day, field, value) => {
    const hours = { ...cfg.hours, [day]: { ...cfg.hours[day], [field]: value } };
    setCfg({ ...cfg, hours });
  };
  const commitDay = (day) => saveCfg({ hours: { [day]: cfg.hours[day] } });

  const addBlock = async () => {
    if (!form.start_date) { toast.error("Pick a start date."); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/schedule/blocks`, form, auth);
      toast.success(data.existing_appointments_flagged > 0
        ? `Added. ${data.existing_appointments_flagged} existing appointment(s) now conflict — flagged below for you to reschedule.`
        : "Block added.");
      setForm({ start_date: "", end_date: "", full_day: true, start_time: "12:00", end_time: "15:00", label: "" });
      load();
    } catch (err) { toast.error(detailMsg(err, "Could not add block.")); }
    finally { setSaving(false); }
  };

  const removeBlock = async (id) => {
    try { await axios.delete(`${API}/schedule/blocks/${id}`, auth); toast.success("Block removed — day reopened."); load(); }
    catch { toast.error("Could not remove block."); }
  };

  const resolveConflict = async (id) => {
    try { await axios.post(`${API}/schedule/conflicts/${id}/resolve`, {}, auth); toast.success("Marked as handled."); load(); }
    catch { toast.error("Could not update."); }
  };

  if (loading || !cfg) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#F2B705]" /></div>;

  const inputCls = "bg-transparent border-b border-white/15 focus:border-[#F2B705] outline-none py-1 text-sm rounded-none [color-scheme:dark] w-[88px]";
  const lastStart = (() => {
    // Mirrors the backend's "last start = close - slot_minutes" rule, for the explainer text only.
    const sample = Object.values(cfg.hours)[0];
    if (!sample) return "";
    const [h, m] = sample.close.split(":").map(Number);
    const total = h * 60 + m - cfg.slot_minutes;
    return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
  })();

  return (
    <div data-testid="schedule-settings" className="space-y-8">
      <div className="bg-[#0C0C0C] border border-[#F2B705]/25 rounded-xl p-4">
        <div className="flex items-center gap-3 mb-1">
          <div className="p-2 rounded-lg bg-[#F2B705]/15 text-[#F2B705]"><AlertTriangle size={18} /></div>
          <div className="text-white font-semibold">Pickups and returns are appointment-only</div>
        </div>
        <p className="text-white/55 text-sm">
          Even if a car is free, a customer can only pick a pickup or return time inside your open hours below —
          every <b>{cfg.slot_minutes}-minute</b> slot shares <b>{cfg.capacity}</b> handoff{cfg.capacity === 1 ? "" : "s"} of capacity across
          BOTH pickups and returns, with a <b>{cfg.buffer_minutes}-minute</b> buffer between appointments.
          The last bookable start time each day is <b>{lastStart}</b> (so the handoff still finishes by closing time).
        </p>
      </div>

      {/* Recurring weekly hours */}
      <div>
        <h4 className="flex items-center gap-2 font-display font-bold mb-3"><Clock size={16} /> Recurring Weekly Hours</h4>
        <div className="bg-[#0C0C0C] border border-white/10 rounded-lg p-4 space-y-2" data-testid="recurring-hours-form">
          {DAY_ORDER.map((d) => (
            <div key={d} className="flex items-center gap-4 py-1.5 border-b border-white/5 last:border-0">
              <button data-testid={`day-toggle-${d}`} onClick={() => { setDayHour(d, "enabled", !cfg.hours[d].enabled); saveCfg({ hours: { [d]: { ...cfg.hours[d], enabled: !cfg.hours[d].enabled } } }); }}
                className={`text-xs px-3 py-1.5 rounded-md font-semibold border w-[108px] ${cfg.hours[d].enabled ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-white/5 text-white/40 border-white/10"}`}>
                {DAY_LABELS[d]}
              </button>
              {cfg.hours[d].enabled ? (
                <div className="flex items-center gap-2 text-sm text-white/70">
                  <input data-testid={`day-open-${d}`} type="time" className={inputCls} value={cfg.hours[d].open}
                    onChange={(e) => setDayHour(d, "open", e.target.value)} onBlur={() => commitDay(d)} />
                  <span>to</span>
                  <input data-testid={`day-close-${d}`} type="time" className={inputCls} value={cfg.hours[d].close}
                    onChange={(e) => setDayHour(d, "close", e.target.value)} onBlur={() => commitDay(d)} />
                </div>
              ) : <span className="text-white/35 text-sm">Closed all day</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Slot / capacity / buffer settings */}
      <div>
        <h4 className="flex items-center gap-2 font-display font-bold mb-3"><Settings2 size={16} /> Slot Settings</h4>
        <div className="bg-[#0C0C0C] border border-white/10 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-[11px] text-white/45 block mb-1">Appointment length (minutes)</label>
            <input data-testid="slot-minutes-input" type="number" min="5" step="5" value={cfg.slot_minutes}
              className="bg-black border border-white/15 rounded px-3 py-2 text-sm w-full outline-none focus:border-[#F2B705]"
              onChange={(e) => setCfg({ ...cfg, slot_minutes: Number(e.target.value) || 30 })}
              onBlur={(e) => saveCfg({ slot_minutes: Number(e.target.value) || 30 })} />
          </div>
          <div>
            <label className="text-[11px] text-white/45 block mb-1">Capacity per slot (shared, pickups + returns)</label>
            <input data-testid="capacity-input" type="number" min="1" step="1" value={cfg.capacity}
              className="bg-black border border-white/15 rounded px-3 py-2 text-sm w-full outline-none focus:border-[#F2B705]"
              onChange={(e) => setCfg({ ...cfg, capacity: Number(e.target.value) || 1 })}
              onBlur={(e) => saveCfg({ capacity: Number(e.target.value) || 1 })} />
          </div>
          <div>
            <label className="text-[11px] text-white/45 block mb-1">Buffer between appointments (minutes)</label>
            <input data-testid="buffer-minutes-input" type="number" min="0" step="5" value={cfg.buffer_minutes}
              className="bg-black border border-white/15 rounded px-3 py-2 text-sm w-full outline-none focus:border-[#F2B705]"
              onChange={(e) => setCfg({ ...cfg, buffer_minutes: Number(e.target.value) || 0 })}
              onBlur={(e) => saveCfg({ buffer_minutes: Number(e.target.value) || 0 })} />
          </div>
        </div>
      </div>

      {/* Date-specific blocks */}
      <div>
        <h4 className="flex items-center gap-2 font-display font-bold mb-3"><CalendarOff size={16} /> Date-Specific Closures (full day, partial day, or vacation range)</h4>
        <div className="bg-[#0C0C0C] border border-white/10 rounded-lg p-4 space-y-3" data-testid="schedule-block-form">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[11px] text-white/45 block">From</label>
              <input type="date" data-testid="block-from-input" className="bg-transparent border-b border-white/15 focus:border-[#F2B705] outline-none py-1.5 text-sm w-full [color-scheme:dark]"
                value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} /></div>
            <div><label className="text-[11px] text-white/45 block">To (optional — same day if blank)</label>
              <input type="date" data-testid="block-to-input" className="bg-transparent border-b border-white/15 focus:border-[#F2B705] outline-none py-1.5 text-sm w-full [color-scheme:dark]"
                value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} /></div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-white/70">
              <input type="radio" data-testid="block-fullday-radio" checked={form.full_day} onChange={() => setForm((f) => ({ ...f, full_day: true }))} /> Full day(s)
            </label>
            <label className="flex items-center gap-2 text-sm text-white/70">
              <input type="radio" data-testid="block-partial-radio" checked={!form.full_day} onChange={() => setForm((f) => ({ ...f, full_day: false }))} /> Partial day
            </label>
            {!form.full_day && (
              <div className="flex items-center gap-2">
                <input type="time" data-testid="block-start-time-input" className={inputCls} value={form.start_time} onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))} />
                <span className="text-white/40 text-xs">to</span>
                <input type="time" data-testid="block-end-time-input" className={inputCls} value={form.end_time} onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))} />
              </div>
            )}
          </div>
          <input data-testid="block-label-input" placeholder="e.g. Owner vacation" className="bg-transparent border-b border-white/15 focus:border-[#F2B705] outline-none py-1.5 text-sm w-full"
            value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} />
          <button data-testid="block-submit-btn" onClick={addBlock} disabled={saving}
            className="bg-[#F2B705] text-black text-sm font-bold px-4 py-2 rounded-md hover:bg-white transition-colors flex items-center gap-1.5 disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <CalendarOff size={14} />} Add Closure
          </button>
          <p className="text-white/40 text-xs">This only blocks NEW pickup/return handoff appointments on these dates — it never touches a vehicle's rental availability. A rental can still span these days if its pickup and return times are both outside the closure.</p>
        </div>

        <div className="space-y-1.5 mt-4" data-testid="schedule-blocks-list">
          {blocks.length === 0 && <p className="text-white/35 text-sm">No closures scheduled.</p>}
          {blocks.map((b) => (
            <div key={b.id} className="flex items-center justify-between gap-2 bg-[#0A0A0A] border border-red-500/20 rounded px-3 py-2 text-sm">
              <span>
                <span className="text-red-400">{b.start_date}{b.end_date !== b.start_date ? ` → ${b.end_date}` : ""}</span>
                <span className="text-white/50"> · {b.full_day ? "full day" : `${b.start_time}–${b.end_time}`}{b.label ? ` · ${b.label}` : ""}</span>
              </span>
              <button data-testid={`remove-block-${b.id}`} onClick={() => removeBlock(b.id)}
                className="shrink-0 text-xs flex items-center gap-1.5 border border-red-400/40 text-red-400 px-3 py-1.5 rounded hover:bg-red-500 hover:text-white transition-colors">
                <Trash2 size={13} /> Reopen
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Flagged conflicts */}
      <div>
        <h4 className="flex items-center gap-2 font-display font-bold mb-3 text-amber-400"><AlertTriangle size={16} /> Appointments Needing Your Reschedule</h4>
        <p className="text-white/40 text-xs mb-3">These existing pickup/return appointments now fall inside a closure you added. Nothing was moved, cancelled, or charged automatically — reschedule each one yourself (Reservations/Bookings), then mark it handled.</p>
        <div className="space-y-1.5" data-testid="schedule-conflicts-list">
          {conflicts.length === 0 && <p className="text-white/35 text-sm flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-400" /> None right now.</p>}
          {conflicts.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-2 bg-[#0A0A0A] border border-amber-500/25 rounded px-3 py-2 text-sm">
              <span>
                <span className="text-amber-300 font-semibold">{c.name}</span> · {c.vehicle} ·{" "}
                {c.schedule_conflict?.hits?.includes("pickup") && <span>pickup {c.pickup_date} {c.pickup_time}</span>}
                {c.schedule_conflict?.hits?.includes("pickup") && c.schedule_conflict?.hits?.includes("return") && " & "}
                {c.schedule_conflict?.hits?.includes("return") && <span>return {c.return_date} {c.return_time}</span>}
                <span className="text-white/40"> · {c.schedule_conflict?.block_label}</span>
              </span>
              <button data-testid={`resolve-conflict-${c.id}`} onClick={() => resolveConflict(c.id)}
                className="shrink-0 text-xs flex items-center gap-1.5 border border-emerald-400/40 text-emerald-400 px-3 py-1.5 rounded hover:bg-emerald-500 hover:text-white transition-colors">
                <CheckCircle2 size={13} /> Mark Handled
              </button>
            </div>
          ))}
        </div>
      </div>
      {/* Stale handoff-slot reservations (e.g. left behind by a reset/cancelled extension) */}
      <div>
        <h4 className="flex items-center gap-2 font-display font-bold mb-3 text-red-400"><Wrench size={16} /> Stale Handoff-Slot Reservations</h4>
        <p className="text-white/40 text-xs mb-3">
          A slot can get stuck "fully booked" if an extension that once moved a return handoff was later reset/cancelled before this fix.
          This only flags a slot whose reserved count is HIGHER than the active appointments that genuinely need it right now — it never
          raises capacity, never touches a booking's own dates or charges, and a real conflict (an appointment that's still active) is
          never shown here.
        </p>
        <div className="space-y-1.5" data-testid="stale-slots-list">
          {staleLoading && <p className="text-white/35 text-sm flex items-center gap-1.5"><Loader2 className="animate-spin" size={13} /> Checking…</p>}
          {!staleLoading && staleSlots.length === 0 && <p className="text-white/35 text-sm flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-400" /> No stale reservations found.</p>}
          {staleSlots.map((row) => {
            const key = `${row.date}T${row.time}`;
            return (
              <div key={key} data-testid={`stale-slot-${key}`} className="flex items-center justify-between gap-2 bg-[#0A0A0A] border border-red-500/25 rounded px-3 py-2 text-sm">
                <span>
                  <span className="text-red-300 font-semibold">{row.date} {row.time}</span>
                  <span className="text-white/50"> · reserved {row.stored_count}, actually needed {row.true_count}</span>
                  <span className="text-red-400/80"> · {row.stale_reservations} orphaned</span>
                </span>
                <button data-testid={`repair-stale-slot-${key}`} onClick={() => repairSlot(row)} disabled={repairing === key}
                  className="shrink-0 text-xs flex items-center gap-1.5 border border-red-400/40 text-red-400 px-3 py-1.5 rounded hover:bg-red-500 hover:text-white transition-colors disabled:opacity-60">
                  {repairing === key ? <Loader2 className="animate-spin" size={13} /> : <Wrench size={13} />} Repair This Slot
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
